import { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  Box,
  Checkbox,
  VStack,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';

interface Driver {
  id: number | string;
  name: string;
}

interface MvrConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (consentConfirmed: boolean) => void;
  isLoading?: boolean;
  drivers: Driver[];
  carrierName: string;
}

const MvrConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  drivers,
  carrierName,
}: MvrConfirmModalProps) => {
  const [isConsentChecked, setIsConsentChecked] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsConsentChecked(false);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="lg" p={2}>
        <ModalCloseButton />

        <ModalBody pt={8} pb={4}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="xl" fontWeight="bold">
              Confirm MVR Request
            </Text>

            <Text>
              You have chosen to proceed with{' '}
              <Text as="span" fontWeight="700">
                {carrierName}
              </Text>
              .
            </Text>

            <Text>MVR reports will be ordered for the following drivers:</Text>

            <UnorderedList spacing={1} pl={4} color="red.600">
              {drivers.map((driver) => (
                <ListItem key={driver.id} fontWeight="500">
                  {driver.name}
                </ListItem>
              ))}
            </UnorderedList>
          </VStack>
        </ModalBody>

        <Box mx={6} p={4} borderRadius="md" borderWidth="1px">
          <Text fontWeight="600" mb={3}>
            Please confirm the following:
          </Text>
          <Checkbox
            isChecked={isConsentChecked}
            onChange={(e) => setIsConsentChecked(e.target.checked)}
            colorScheme="blue"
            alignItems="flex-start"
          >
            <Text fontSize="sm" fontStyle="italic" color="gray.600" mt={-0.5}>
              I confirm that I have received authorization from the client(s) to
              obtain, verify, and exchange personal information for the purposes
              of underwriting, rating, and issuing insurance, including policy
              and driver history reports from the appropriate authorities and
              databases.
            </Text>
          </Checkbox>
        </Box>

        <ModalFooter gap={3} pt={6}>
          <Button
            colorScheme="brand"
            onClick={() => onConfirm(isConsentChecked)}
            isLoading={isLoading}
            isDisabled={!isConsentChecked}
            minW="100px"
          >
            Confirm
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            isDisabled={isLoading}
            minW="100px"
          >
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MvrConfirmModal;
